<template>
  <div class="layout-regions">
    <MobileHeader
      :class="{ hide: showAllElements }"
    >
      <template #action>
        <OperationTypeNavigationMobile :showHmePolicy="currentShaft?.policy?.show_heavy_mobile_equipment" />
      </template>
      <div
        v-if="hasIssueTypeParam"
        class="menu"
      >
        <ul class="menu__items">
          <li class="menu-item">
            <button
              :aria-label="$t('regions.search_regions_and_issues')"
              class="menu-item__link menu-item__link--icon"
              :disabled="issueList.length === 0"
              type="button"
              @click="openMobileSearch"
            >
              <div class="menu-item__label">
                <UseSvg
                  class="icon"
                  name="search"
                />
              </div>
            </button>
          </li>
        </ul>
      </div>
    </MobileHeader>
    <Select
      v-if="setHasIssues"
      ref="select"
      v-model="selectedSet"
      :class="{ 'hide-for-medium': true, hide: showAllElements }"
      :items="setsWithIssuesList"
      :modifiers="['input', 'base', 'set-select']"
      :placeholder="$t('shafts.3dModel.select_set')"
      :showTotalInLabel="false"
    />
    <div
      :class="{
        'layout-regions__header hide-for-medium': true,
        hide: showAllElements,
      }"
    >
      <button
        class="select__trigger typo-ignore typo-title"
        type="button"
        @click="showAllElements = true"
      >
        <div class="select__label-wrapper">
          <span
            v-if="selectedType || issueList?.length > 0"
            class="select__label"
          >
            {{ itemTitle || $t('shafts.issues.all_elements') }}
          </span>
          <span
            v-else
            class="select__label"
          >
            {{ itemTitle || $t('shafts.issues.all_issues') }}
          </span>
        </div>
        <div class="select__arrow">
          <UseSvg
            class="icon"
            name="filter"
          />
        </div>
      </button>
      <SwitchButton
        v-model="view"
        :items="viewOptions"
      />
    </div>

    <div
      :class="{
        'layout-regions__regions': true,
        'show-for-medium': !showAllElements,
      }"
    >
      <OperationTypeNavigation :showHmePolicy="currentShaft?.policy?.show_heavy_mobile_equipment" />
      <div class="layout-regions__regions-header hide-for-medium">
        <button
          type="button"
          @click="showAllElements = false"
        >
          <UseSvg
            class="icon"
            name="close"
          />
        </button>
        <span class="layout-regions__regions-title">
          {{ $t('shafts.issues.select_an_element') }}
        </span>
      </div>

      <div class="layout-regions__search-bar show-for-medium">
        <UseSvg
          :class="{ icon: true, 'icon--darker': !elementSearch }"
          name="search"
        />
        <input
          v-model="elementSearch"
          class="layout-regions__search-input typo-small-title"
          :disabled="issueList.length === 0"
          :placeholder="$t('search.enter_search_text')"
        >
        <button
          v-if="elementSearch"
          class="button button--tiny button--transparent"
          type="button"
          @click="elementSearch = ''"
        >
          <UseSvg
            class="icon icon--small"
            name="close_circle_16"
          />
        </button>
      </div>

      <EmptyMessage
        v-if="!setHasIssues && !loadingSetsWithIssues"
        :message="panelEmptyStateMessage"
        :modifiers="['grow', 'center']"
      />
      <SkeletonElement
        v-if="loadingSetsWithIssues"
        :height="40"
        :radius="6"
        style="margin: 8px"
      />
      <Select
        v-if="setHasIssues && !elementSearch && !loadingSetsWithIssues"
        ref="select"
        v-model="selectedSet"
        :items="setsWithIssuesList"
        :modifiers="['input', 'base', 'set-select', 'short-dropdown']"
        :placeholder="$t('shafts.3dModel.select_set')"
        :showTotalInLabel="false"
      />
      <StationLink
        v-if="setOnStation"
        :station="setOnStation"
      />
      <RegionTreeSkeletonLoader v-if="loadingSetTree" />
      <RegionTree
        v-if="currentSetTreeElements && !elementSearch"
        :modifiers="['manage-scroll']"
        :regionTree="currentSetTreeElements.shaft_element_types"
        :selectAllLabel="$t('shafts.issues.all_elements')"
        :selectedRegion="selectedType"
        @select="newSelection"
      />

      <EmptyMessage
        v-if="elementSearch && elementSearchResults.length === 0"
        :message="$t('general.no_results')"
        :modifiers="['grow']"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>

      <template v-if="elementSearch && elementSearchResults.length > 0">
        <div class="layout-regions__results-header typo-small">
          {{ $t('general.results') }}
        </div>
        <div class="layout-regions__regions-tree scroll-container">
          <ul>
            <RegionItem
              v-for="region in elementSearchResults"
              :key="region.id"
              class="layout-regions__regions-tree"
              :ignoreSelected="true"
              :region="region"
              :showChildren="false"
              @select="selectRegionFromSearch"
            />
          </ul>
        </div>
      </template>
    </div>

    <div
      :class="{
        'layout-regions__content': true,
        'show-for-medium': showAllElements,
      }"
    >
      <div class="layout-regions__issues">
        <div class="layout-regions__search-bar show-for-medium">
          <UseSvg
            :class="{ icon: true, 'icon--darker': !issueSearch }"
            name="search"
          />
          <input
            v-model="issueSearch"
            class="layout-regions__search-input typo-small-title"
            :disabled="issueList.length === 0"
            :placeholder="$t('shafts.issues.search_issues')"
          >
          <button
            v-if="issueSearch"
            class="button button--tiny button--transparent"
            type="button"
            @click="issueSearch = ''"
          >
            <UseSvg
              class="icon icon--small"
              name="close_circle_16"
            />
          </button>
        </div>

        <NoticeMessage
          v-if="issuesStore.loadingNotification && issuesStore.loading"
          iconStyle="info"
          :message="$t('general.long_load_message')"
          style="margin: 5px"
        />
        <IssuesSkeletonLoader
          v-if="selectedSet && issuesStore.loading"
          class="show-for-medium"
        />
        <div
          v-if="!issuesStore.loading"
          :class="{
            'layout-regions__issues-content': true,
            hide: !selectedSet || issueSearch,
          }"
        >
          <p class="selected-name show-for-medium">
            <span v-if="selectedType || issueList?.length > 0">
              {{ itemTitle || $t('shafts.issues.all_elements') }}
            </span>
            <span v-else>
              {{ itemTitle || $t('shafts.issues.all_issues') }}
            </span>
          </p>
          <div class="layout-regions__issues-header">
            <span
              v-if="shaftIssueList"
              class="layout-regions__issues-count typo-small-nav-item"
            >
              {{ shaftIssueList.length }}
              {{ $t('activerecord.models.plants/issue', { count: shaftIssueList.length }) }}
            </span>

            <div
              v-if="shaftIssueList?.length > 0"
              class="layout-regions__csv-download typo-small"
            >
              <a :href="csvIssueDownloadPath || 'javascript:void(0);'">
                <div class="layout-regions__csv-download__link">
                  <span>{{ $t('general.csv') }}</span>
                  <PushDown />
                </div>
              </a>
            </div>

            <IssueFilter
              v-if="isActiveFilter || shaftIssueList?.length > 0"
              :sortOptions="sortByOptions"
              :typeFilter="false"
              @set-filter-options="setFilterOptions"
            />
          </div>
          <RecyclerView
            v-if="shaftIssueList && shaftIssueList.length > 0"
            ref="issueScrollView"
            :class="{
              'layout-regions__list': true,
              'show-for-medium': view !== 'list',
            }"
            :count="shaftIssueList.length"
            :gap="0.125"
            :height="7"
            hideScrollingX
            name="layout-regions__list-item-"
          >
            <template #default="{ index, style, key }">
              <IssueCard
                :key="key"
                :isLocatable="true"
                isSmallCard
                :issue="shaftIssueList[index]"
                overrideIssuePath="shaftV2RegionsIssuesTypeSet"
                :responsive="false"
                :selected="isCurrentlySelectedIssue(shaftIssueList[index].id)"
                :showDescription="false"
                :style="style"
                @locate="selectIssueOnList(shaftIssueList[index])"
              />
            </template>
          </RecyclerView>
        </div>
        <div
          v-if="issueSearch"
          class="layout-regions__issues-content"
        >
          <EmptyMessage
            v-if="issueSearch && issueResults.length === 0"
            :message="$t('general.no_results')"
          >
            <UseSvg name="magnifying_glass_circle" />
          </EmptyMessage>
          <template v-if="issueSearch && issueResults.length > 0">
            <div class="layout-regions__results-header typo-small">
              {{ $t('general.results') }}
            </div>
            <div class="layout-regions__list scroll-container">
              <ul class="recycler__list">
                <IssueCard
                  v-for="issue in issueResults"
                  :key="issue.id"
                  isSmallCard
                  :issue="issue"
                  noLink
                  overrideIssuePath="shaftV2RegionsIssuesTypeSet"
                  :responsive="false"
                  :showDescription="false"
                  @select="selectIssueFromSearch(issue)"
                />
              </ul>
            </div>
          </template>
        </div>
      </div>

      <div
        :class="{
          'layout-regions__map layout-regions--map layout-regions--model': true,
          'show-for-medium': view !== 'main',
        }"
      >
        <Loader
          v-if="issuesStore.loading"
          class="layout-regions__mobile-issues-loading hide-for-medium"
        />
        <Shaft3dModelLayout :key="issuesStore.componentKey">
          <template
            v-if="setsWithIssuesList.length > 0"
            #toolbar
          >
            <Shaft3dModelOrientation />
            <Shaft3dModelToolbar
              v-model="toolbarValue"
              :disabled="!selectedSet"
              :showSidewallCheckbox="hasSidewalls"
              :sidewallsChecked="sideWallsActive"
              @display-sidewalls="displaySidewalls"
              @update:model-value="selectedAction"
            />
          </template>
          <template
            v-if="setsWithIssuesList.length > 0"
            #navigation
          >
            <Shaft3dModelSetNavigation
              ref="setNav"
              :currentSet="Number($route.params.setNumber)"
              :setsWithIssues="setsWithIssuesList"
              :stations="stationList"
              :totalNumbeOfSets="shaft?.number_of_sets"
              @station-name="updateStationName"
            />
          </template>
          <template #scene>
            <ShaftModelLoader v-if="loadingSetTree" />
            <div
              v-if="selectedSet && !loadingSetElements && !loadingSetTree"
              ref="sceneContainer"
              class="scene-container scene-container--issues"
            >
              <div
                v-if="elementHoverName"
                class="scene-container__hover-name"
              >
                {{ elementHoverName }}
              </div>
            </div>
            <EmptyMessage
              v-if="!selectedSet && !loadingSetElements"
              :message="panelEmptyStateMessage"
              :modifiers="['grow', 'center']"
              :title="$t('shafts.3dModel.no_set_selection.title')"
            />
          </template>
        </Shaft3dModelLayout>
      </div>
    </div>

    <div
      v-if="mobileSearchOpen"
      class="layout-regions__mobile-search hide-for-medium"
    >
      <div class="layout-regions__search-bar">
        <UseSvg
          class="icon"
          name="search"
        />
        <input
          ref="mobileSearchInput"
          v-model="mobileSearch"
          class="layout-regions__search-input typo-small-title"
          :placeholder="$t('regions.search_regions_and_issues')"
        >
        <UseSvg
          class="icon"
          name="close_circle_24"
          @click="mobileSearchOpen = false"
        />
      </div>
      <EmptyMessage
        v-if="mobileSearch && elementSearchResults.length === 0 && issueResults.length === 0"
        :message="$t('general.no_results')"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
      <ul class="layout-regions__search-results scroll-container recycler__list">
        <RegionItem
          v-for="region in elementSearchResults"
          :key="region.id"
          :ignoreSelected="true"
          :region="region"
          :showChildren="false"
          @select="selectRegionFromSearch"
        />
        <IssueCard
          v-for="issue in issueResults"
          :key="issue.id"
          isSmallCard
          :issue="issue"
          noLink
          overrideIssuePath="shaftV2RegionsIssuesTypeSet"
          :responsive="false"
          :showDescription="false"
          @select="selectIssueFromSearch(issue)"
        />
      </ul>
    </div>
  </div>
  <router-view v-if="['shaftV2RegionsIssuesTypeSet', 'shaftV2IssuesLocationTypeSet'].includes($route.name)" />
</template>

<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import { screenAtLeast } from '@/functions/screen_width';
import { useRouter, useRoute } from 'vue-router';
import { useShaftV2 } from '@/functions/use_remote_object';
import { sortingFunctions, filterBy } from '@/functions/utils.js';
import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import debounce from '@/vendor/debounce';
import Searcher from '@/functions/searcher';
import pluralize from '@/functions/pluralize';
import { useActiveFilter } from '@/functions/composables/active_filter.js';

import { useCheckOperationType } from '@/functions/check_operation_type';
import { useShaft3dModel } from '@/use/useShaft3dModel';
import {
  ShaftElement,
  IssuePoint,
  handlePointPlacement,
  IssuePointCrosshair
} from '@/functions/shaft3dModel';

// Stores
import { useIssuesStore } from '@/stores/issues/issuesStore';

// Components
import MobileHeader from '@/components/generic/mobile_header.vue';
import RegionItem from '@/components/regions/region_item.vue';
import RegionTree from '@/components/regions/region_tree.vue';
import Select from '@/components/form/select.vue';
import SwitchButton from '@/components/form/switch_button.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import Shaft3dModelToolbar from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_toolbar.vue';
import Shaft3dModelOrientation from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_orientation.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';
import ShaftModelLoader from '@/components/shaftsV2/shaft_model_loader.vue';
import Shaft3dModelSetNavigation from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_set_navigation.vue';
import Shaft3dModelLayout from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_layout.vue';
import StationLink from '@/components/shaftsV2/generic/station_link.vue';
import RegionTreeSkeletonLoader from '@/components/regions/region_tree_skeleton_loader.vue';
import RecyclerView from '@/components/generic/recycler_view.vue';
import IssueFilter from '@/components/issues/issue_filter.vue';
import OperationTypeNavigation from '@/components/shaftsV2/operations/operation_type_navigation.vue';
import OperationTypeNavigationMobile from '@/components/shaftsV2/operations/operation_type_navigation_mobile.vue';
import IssuesSkeletonLoader from '@/components/issues/issues_skeleton_loader.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';
import Loader from '@/components/generic/loader.vue';

// Icons
import PushDown from '@/images/push-down.svg';

// Set up store
const issuesStore = useIssuesStore();

const props = defineProps({
  issueType: { type: String, required: false, default: null },
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  inspectionId: { type: String, required: false, default: null },
  issueId: { type: String, required: false, default: null },
  setNumber: { type: String, required: false, default: null },
  setId: { type: String, required: false, default: null },
});

const {
  initTHREE,
  SCENE,
  clearScene,
  elementHoverName,
  getAllElements,
  pointSelection,
  resetCamera,
  setElementMaterialType,
  applyConveyanceStyle,
  displaySidewalls,
  flattenElements,
  sideWallsActive,
  focusOnElement,
} = useShaft3dModel();

const ajax = useAjax();
const sortFunctions = sortingFunctions();
const router = useRouter();
const route = useRoute();
const currentShaft = useShaftV2({ organisationId: route.params.organisationId, shaftId: route.params.shaftId });

const { isSet } = useCheckOperationType();

const currentSetTreeElements = ref(null);
const currentSetElements = ref(null);
const loadingSetTree = ref(false);
const elementSearch = ref('');
const issueSearch = ref('');
const selectedSet = ref(null);
const selectedType = ref(null);
const shaft = ref(null);
const setsWithIssues = ref(null);
const loadingSetsWithIssues = ref(false);
const showAllElements = ref(false);
const view = ref('main');

const issueList = ref([]);
const sceneContainer = ref();
const selectedIssue = ref(null);
const loadingSetElements = ref(false);

const toolbarValue = ref(null);

const issueResults = ref([]);
const elementSearchResults = ref([]);

const mobileSearchOpen = ref(false);
const mobileSearch = ref('');
const mobileSearchInput = ref(null);

const sortBy = ref('');
const { isActiveFilter, validateFilterOptions } = useActiveFilter();

let issuesSearcher = null;
let regionsSearcher = null;

const issueScrollView = ref(null);
const stationList = ref(null);
const setNav = ref(null);

const conditions = ref([]);
const priorities = ref([]);
const setOnStation = ref(null);
const routerNameList = ['shaftV2IssuesTypeSets', 'shaftV2IssuesTypeSet'];

const viewOptions = [
  { icon: 'crosshair_dot', value: 'main' },
  { icon: 'list_bullet_end', value: 'list' },
];

const sortByOptions = [
  { label: I18n.t('issues.highest_risk'), value: 'highest_risk'},
  { label: I18n.t('issues.lowest_risk'), value: 'lowest_risk'},
  { label: I18n.t('general.oldest'), value: 'oldest'},
  { label: I18n.t('general.newest'), value: 'newest'},
  { label: I18n.t('issues.highest_repair_priority'), value: 'highest_repair_priority'},
  { label: I18n.t('issues.lowest_repair_priority'), value: 'lowest_repair_priority'},
];

const hasIssueTypeParam = computed(() => {
  return isSet.value;
});

const setHasIssues = computed(() => {
  return setsWithIssuesList.value.length > 0;
});

const panelEmptyStateMessage = computed(() => {
  if (setHasIssues.value) {
    return I18n.t('empty_states.sets.select_a_set');
  }

  return I18n.t('empty_states.sets.no_issues');
});

const baseUrl = `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`;
const ENDPOINTS = computed(() => {
  return {
    shaft: baseUrl,
    setElements: `${baseUrl}/sets`,
    stations: `${baseUrl}/stations`,
  };
});

const setsWithIssuesList = computed(() => {
  let sets = [];

  if (setsWithIssues.value) {
    setsWithIssues?.value.forEach((setItem) => {
      sets.push({
        value: setItem.set,
        label: `${I18n.t('shafts.issues.set')} ${setItem.set}`,
        total: `${pluralize(
          setItem.count,
          I18n.t('activerecord.models.plants/issue.one'),
          I18n.t('activerecord.models.plants/issue.other')
        )}`,
      });
    });
  }

  return sets;
});

const itemTitle = computed(() => {
  let translationTypes = I18n.t('shafts.element_types.shaft');

  return translationTypes[selectedType.value?.name] || selectedType.value?.name;
});

const hasSidewalls = computed(() => {
  return currentSetTreeElements.value?.shaft_element_types.some(
    (e) => e.name === 'sidewalls'
  );
});

onMounted(async () => {
  await loadShaft();
  await loadStations();
  loadSetsWithIssues();
  displaySidewalls(true);
  updateSetSelector(route.params.setNumber);
});

async function loadStations() {
  if (route.params.shaftId) {
    let { ok, data } = await ajax(ENDPOINTS.value.stations);

    if (ok) {
      stationList.value = data.map((item) => ({
        label: item.name,
        value: item.id,
        diagram: item.diagram_identifier,
        name: item.name,
        start_set: item.start_set,
        end_set: item.end_set,
        id: item.id
      }));
    }
  }
}

async function updateSetSelector(setNumber) {
  if (!isIncluded(routerNameList, route.name)) return;
  if (setNumber) {
    selectedType.value = null;
    selectedSet.value = Number(setNumber);
    currentSetTreeElements.value = null;
    await loadSetTreeData();
    await load3dModel();
    getAllIssues();

    issuesSearcher = new Searcher(issueList.value, { keys: ['title', 'id'] });
    regionsSearcher = new Searcher(currentSetTreeElements.value?.shaft_element_types);
  }
}

function getAllIssues() {
  issueList.value = [];
  currentSetElements.value?.shaft_element_set?.forEach((set) => {
    set?.issues.forEach((issue) => {
      issueList.value.push({
        ...issue,
        timestamp: +new Date(issue.inspection.date),
      });
    });
  });

  resetModelElements();
}

watch(issuesStore, () => {
  renderIssues();
});

watch(() => props.issueType, async() => {
  selectedSet.value = null;
  currentSetTreeElements.value = null;
  mobileSearchOpen.value = false;
  mobileSearch.value = '';
  elementSearchResults.value = [];
  issueResults.value = [];
  issueList.value = [];

  if (screenAtLeast('medium') && !route.params.setNumber) {
    router.replace({
      name: 'shaftV2IssuesTypeSets',
      params: { issueType: 'sets' },
    });
  }
});

watch(
  mobileSearch,
  debounce(
    () => {
      if (!mobileSearchOpen.value || !mobileSearch.value) return;
        elementSearchResults.value = regionsSearcher.search(mobileSearch.value);
        issueResults.value = issuesSearcher.search(mobileSearch.value);
    }, 300, true
  )
);

watch(
  issueSearch,
  debounce(
    () => {
      if (!issueSearch.value) return;
      issueResults.value = issuesSearcher.search(issueSearch.value);
    }, 300, true
  )
);

watch(
  elementSearch,
  debounce(
    () => {
      if (!elementSearch.value) return;
      elementSearchResults.value = regionsSearcher.search(elementSearch.value);
    }, 300, true
  )
);

/**
 * watcher for set dropdown
 */
watch(selectedSet, () => {
  if (route.params.issueType !== 'sets') return;
  router.push({
    name: 'shaftV2IssuesTypeSet',
    params: { setNumber: selectedSet.value },
  });
  updateSetSelector(selectedSet.value);
});

/**
 * This is for the 3D-shaft nav interaction
 */
watch(route, async() => {
  setOnStation.value = null;
  await clearScene(SCENE);
  await initTHREE(sceneContainer.value, shaft.value.dimensions);
  await renderElements();
  await getAllIssues();

  if (setNav.value?.updateSet && route.params.setNumber) {
    updateSetSelector(route.params.setNumber);
    setNav.value.updateSet = false;
  }
});

async function loadShaft() {
  if (props.shaftId) {
    let { ok, data } = await ajax(ENDPOINTS.value.shaft);

    if (ok) {
      shaft.value = data;
    }
  }
}

async function renderIssues() {
  currentSetElements.value = issuesStore.issues;

  issuesSearcher = new Searcher(issueList.value, { keys: ['title', 'id'] });
}

async function load3dModel() {
  await loadSetElements();
  await clearScene(SCENE);
  await initTHREE(sceneContainer.value, shaft.value.dimensions);
  renderElements();
}

async function loadSetTreeData() {
  loadingSetTree.value = true;
  let { ok, data } = await ajax(
    `${ENDPOINTS.value.setElements}/${selectedSet.value || props.setNumber}?group_by=shaft_element_type`
  );

  if (ok) {
    currentSetTreeElements.value = data;
  }
  loadingSetTree.value = false;
}

async function loadSetElements() {
  loadingSetElements.value = true;

  issuesStore.$reset();

  setTimeout(() => {
    issuesStore.loadingNotification = true;
  }, 5000);

  let { ok, data } = await ajax(
    `${ENDPOINTS.value.setElements}/${selectedSet.value || props.setNumber}`
  );

  if (ok) {
    issuesStore.issues = data;
    renderIssues();
  }
  loadingSetElements.value = false;
  issuesStore.setLoadingToFalse();
}

async function loadSetsWithIssues() {
  loadingSetsWithIssues.value = true;
  let { ok, data } = await ajax(`${ENDPOINTS.value.setElements}`);

  if (ok) {
    setsWithIssues.value = data;
  }
  loadingSetsWithIssues.value = false;
}

function renderElements() {
  currentSetElements.value?.shaft_element_set?.forEach((set) => {
    let setElement = new ShaftElement(set.shaft_element, shaft.value);

    set.issues.forEach((issue) => {
      let point = new IssuePoint(issue);
      handlePointPlacement(
        setElement,
        point,
        shaft.value.dimensions,
        issue.location.position
      );
    });

    SCENE.add(setElement);
  });

  applyConveyanceStyle(SCENE);
}

/**
 * watcher for selecting an element with the click handler
 */
watch(pointSelection, async () => {
  if (pointSelection) {
    currentSetElements.value?.shaft_element_set.forEach((set) => {
      set.issues.forEach((issue) => {
        if (issue.id === pointSelection.value.userData.id) {
          selectedIssue.value = issue;
          setCrosshairOnIssue(issue);
        }
      });
    });
  }
});

function newSelection(region) {
  issueList.value = [];
  selectedIssue.value = null;

  if (region === null) {
    deselectPreviousSelection(selectedType.value);
    selectedType.value = null;
    showAllElements.value = false;
    getAllIssues();
    resetCamera();
    displaySidewalls(true);
  } else {

    displaySidewalls(region.name === 'sidewalls' || region.element_type === 'sidewalls');

    deselectPreviousSelection(selectedType.value);
    selectedType.value = region;
    selectedType.value.selected = true;
    showAllElements.value = false;

    getAllElements(SCENE).forEach((element) => {
      setElementMaterialType(element, 'ghosted');
      applyConveyanceStyle(SCENE);
      element.children.forEach((child) => {
        child.visible = false;
      });

      if (
        element.userData.type === region.name ||
        element.name === region.name ||
        element.userData.group === region.name
      ) {
        setElementMaterialType(element);

        element.children.forEach((child) => {
          child.visible = true;
        });
      }
    });

    currentSetElements.value?.shaft_element_set.forEach((set) => {
      if (
        set.shaft_element.element_type === selectedType.value.name ||
        set.shaft_element.name === selectedType.value.name ||
        set.shaft_element.shaft_element_group?.name === selectedType.value.name
      ) {
        issueList.value.push(...set.issues);
      }
    });
  }
}

function deselectPreviousSelection(currentSelection) {
  if (currentSelection) {
    currentSelection.selected = false;
    clearExistingCrosshair();
  } else {
    return;
  }
}

function resetModelElements() {
  getAllElements(SCENE).forEach((element) => {
    setElementMaterialType(element);
    element.children.forEach((child) => (child.visible = true));
  });

  applyConveyanceStyle(SCENE);
}

async function selectIssueOnList(issue) {
    selectedIssue.value = issue;
    setCrosshairOnIssue(issue);
}

function setCrosshairOnIssue(issue) {
  clearExistingCrosshair();
  resetCamera();
  addCrosshair(issue);
}

/**
 * Adds the crosshair to the selected issue point
 */
function addCrosshair(issue) {
  getAllElements(SCENE).forEach((element) => {
    element.children.forEach((child) => {
      if(child.userData.id === issue.id) {
        focusOnElement(child);
        new IssuePointCrosshair(child, shaft.value);
      }
    });
  });
}

/**
 * Clears existing crosshair from the issue point
 */
function clearExistingCrosshair() {
  getAllElements(SCENE).forEach((element) => {
    element.children.forEach((child) => {
      if (child.userData.type === 'point') {
        child.children = [];
      }
    });
  });
}

/**
 * Returns true/false if current issue is selected
 * @param {string} issueId
 */
function isCurrentlySelectedIssue(issueId) {
  return issueId === selectedIssue.value?.id;
}

// TODO: extract toolbar function into reusable functions
/**
 * Gets the selected toolbar option and
 * calls the relevant method to update the view
 */
function selectedAction(event) {
  flattenElements(event);
  event === 'reset' && resetCamera();
}

async function selectIssueFromSearch(issue) {
  mobileSearchOpen.value = false;
  issueSearch.value = '';
  view.value = 'list';
  selectIssueOnList(issue);
}

async function selectRegionFromSearch(region) {
  mobileSearchOpen.value = false;
  elementSearch.value = '';
  view.value = 'main';
  newSelection(region);
}

async function openMobileSearch() {
  mobileSearchOpen.value = true;
  mobileSearch.value = '';
  elementSearchResults.value = [];
  issueResults.value = [];

  await nextTick();
  mobileSearchInput.value.focus();
}

const csvIssueDownloadPath = computed(() => {
  let endpoint = `/internal/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/graph/download?type=multiple`;

  endpoint += conditions.value.map((item) => `&conditions[]=${item}`).join('');
  endpoint += priorities.value.map((item) => `&priorities[]=${item}`).join('');

  endpoint += `&issue_type=set&set_number=${selectedSet.value || props.setNumber}`;
  if (selectedType.value?.id) {
    endpoint += `&${selectedType.value['type']}_id=${selectedType.value['id']}`;
  } else if (selectedType.value?.name) {
    endpoint += `&${selectedType.value['type']}_name=${selectedType.value['name']}`;
  }

  if (issueList.value?.length > 0) {
    const earliestIssue = issueList.value.reduce((start, current) => {
      return start['inspection']['date'] < current['inspection']['date'] ? start : current;
    });

    if (earliestIssue) {
      endpoint += `&start_date=${earliestIssue['inspection']['date']}`;
    }
  }

  return endpoint;
});

function setFilterOptions(options) {
  sortBy.value = options.sortOption;
  conditions.value = options.conditions;
  priorities.value = options.priorities;

  /* Check if filter has any active filter options */
  validateFilterOptions(options);
}

const shaftIssueList = computed(() => {
  return filteredIssueList();
});

function filteredIssueList(passedIssues) {
  const issues = passedIssues || issueList.value;

  if (!issues) return null;

  let list = [...issues.values()];

  if (conditions.value.length > 0) {
    list = list.filter(i => {
      return conditions.value.includes(i.overall_condition_rating);
    });
  }

  if (priorities.value.length > 0) {
    list = list.filter(i => {
      return priorities.value.includes(i.priority);
    });
  }

  if (sortBy.value === 'highest_repair_priority' || sortBy.value === 'lowest_repair_priority') {
    const highList = filterBy(list, 'priority', 'X');
    const lowList = filterBy(list, 'priority', 'M');
    const numberList = filterBy(list, 'priority')?.sort(sortFunctions[sortBy.value]);
    let finalList = [];

    if (sortBy.value === 'highest_repair_priority') {
      finalList = [...highList, ...numberList, ...lowList];
    } else if (sortBy.value === 'lowest_repair_priority') {
      finalList = [...lowList, ...numberList, ...highList];
    }
    return finalList;
  } else {
    return list.sort(sortFunctions[sortBy.value]);
  }
}

function updateStationName(data) {
  setOnStation.value = JSON.parse(data);
}

function isIncluded(list, name) {
  return list.includes(name);
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.layout-regions--map {
  position: static;
}
.layout-regions--model {
  background-color: map-get($background-color, darkest);
  overflow: hidden;
}

.scene-container--issues {
  height: calc(100vh - rem(250));

  @include breakpoint(medium) {
    height: 100vh;
  }
}
.selected-name {
  margin: bu(1) bu(1.4);
  font-weight: 500;
}
</style>
